import React, { useMemo, memo, useCallback, useRef } from "react"
import Box from "@mui/material/Box"
import { useInfoStore } from "../../../store/userInfo";
import { ButtonBase, Divider, Typography } from "@mui/material";
import { useOrderStore } from "../../../store/order";
import { IOrderModifiers } from "../../../types/menu";
import { useTranslation } from "react-i18next";
import { useWindowDimensions } from "../../../App";
import { useMenuStore } from "../../../store/menuState";
import { logger } from "../../../utils/logger";

const isIOSSafari = /iPhone|iPad|iPod/i.test(navigator.userAgent) && /WebKit/i.test(navigator.userAgent) && !/(CriOS|FxiOS|OPiOS|mercury)/i.test(navigator.userAgent);

const MinusIcon = ({ fill }: { fill: string }) => (
  <svg width="26" height="26" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: "pointer", width: "26px", height: "26px", maxWidth: "100%" }}>
  <g id="minus (1)" clipPath="url(#clip0_7063_8880)">
  <g id="Group">
  <g id="Group_2">
  <path id="Vector" d="M15 0C6.72967 0 0 6.7285 0 15C0 23.2715 6.72967 30 15 30C23.2715 30 30 23.2715 30 15C30 6.7285 23.2703 0 15 0ZM15 27.6762C8.01006 27.6762 2.32377 21.9899 2.32377 15C2.32377 8.01006 8.01006 2.32377 15 2.32377C21.9899 2.32377 27.6762 8.01006 27.6762 15C27.6762 21.9899 21.9899 27.6762 15 27.6762Z" fill={fill}/>
  </g>
  </g>
  <g id="Group_3">
  <g id="Group_4">
  <path id="Vector_2" d="M20.8091 13.7334H9.19023C8.54887 13.7334 8.02832 14.2539 8.02832 14.8953C8.02832 15.5367 8.54887 16.0572 9.19023 16.0572H20.8091C21.4505 16.0572 21.9711 15.5367 21.9711 14.8953C21.9711 14.2539 21.4505 13.7334 20.8091 13.7334Z" fill={fill}/>
  </g>
  </g>
  </g>
  <defs>
  <clipPath id="clip0_7063_8880">
  <rect width="30" height="30" fill="white"/>
  </clipPath>
  </defs>
  </svg>
);

const AddIcon = ({ fill }: { fill: string }) => (
  <svg width="26" height="26" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: "pointer", width: "26px", height: "26px", maxWidth: "100%" }}>
  <g id="add (2)" clipPath="url(#clip0_7063_8888)">
  <g id="Group">
  <g id="Group_2">
  <path id="Vector" d="M15 0C6.7285 0 0 6.7285 0 15C0 23.2715 6.7285 30 15 30C23.2715 30 30 23.2703 30 15C30 6.72967 23.2715 0 15 0ZM15 27.6762C8.01123 27.6762 2.32377 21.9899 2.32377 15C2.32377 8.01006 8.01123 2.32377 15 2.32377C21.9888 2.32377 27.6762 8.01006 27.6762 15C27.6762 21.9899 21.9899 27.6762 15 27.6762Z" fill={fill}/>
  </g>
  </g>
  <g id="Group_3">
  <g id="Group_4">
  <path id="Vector_2" d="M20.8091 13.7333H16.1616V9.08574C16.1616 8.44437 15.6422 7.92383 14.9997 7.92383C14.3571 7.92383 13.8378 8.44437 13.8378 9.08574V13.7333H9.19023C8.5477 13.7333 8.02832 14.2538 8.02832 14.8952C8.02832 15.5366 8.5477 16.0571 9.19023 16.0571H13.8378V20.7047C13.8378 21.346 14.3571 21.8666 14.9997 21.8666C15.6422 21.8666 16.1616 21.346 16.1616 20.7047V16.0571H20.8091C21.4517 16.0571 21.9711 15.5366 21.9711 14.8952C21.9711 14.2538 21.4517 13.7333 20.8091 13.7333Z" fill={fill}/>
  </g>
  </g>
  </g>
  <defs>
  <clipPath id="clip0_7063_8888">
  <rect width="30" height="30" fill="white"/>
  </clipPath>
  </defs>
  </svg>
);



// 메모이제이션된 하위 컴포넌트들
const ModifierItem = memo(({ modifier, depth }: { modifier: IOrderModifiers; depth: number }) => {
  const {userInfo, currentLng} = useInfoStore();
  const storeId = userInfo?.storeId;
  const {t} = useTranslation(`${storeId}`);

  return (
  <Box sx={{ display: "flex",flexDirection: "column", width: '100%' }}>
    <Box sx={{ display: "flex",flexDirection: "row", width: '100%' }}>
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: "500",
          flex: 1,
          textAlign: "left",
          color: '#0085FF'
        }}
      >
        {t(`${storeId}:optionGroupNames.${modifier.modifierGroupId}.${currentLng}`)}
        {/* {modifier.modifierGroupName} */}
      </Typography>
    </Box>
    <Box sx={{ display: "flex",flexDirection: "row", width: '100%', paddingLeft: `${depth+1 * 1.2}rem`}}>
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: "500",
          flex: 1,
          textAlign: "left",
        }}
      >
        {t(`${storeId}:optionNames.${modifier.modifierId}.${currentLng}`)}
        {/* {modifier.modifierName} */}
      </Typography>
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: "600",
          color: "#F72E13",
          textAlign: "right",
          width: "4.0rem",
        }}
      >
        x{modifier.quantity}
      </Typography>
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: "600",
          color: "#F72E13",
          textAlign: "right",
          width: "7.0rem",
        }}
      >
        ${(modifier.modifierPrice / 100).toFixed(2)}
      </Typography>
    </Box>
    </Box>
  );
});

const ModifierList = memo(({ modifiers, depth = 0 }: { modifiers: IOrderModifiers[]; depth?: number }) => (
  <>
    {modifiers.map((modifier: IOrderModifiers, index: number) => (
      <React.Fragment key={`modifier-${modifier.modifierId}-${depth}-${index}`}>
        <Box
          sx={{
            paddingLeft: `${depth * 1.2}rem`,
            display: "flex",
            flexDirection: "column",
            justifyContent: 'flex-start',
            alignItems: "flex-start",
          }}
        >
          <ModifierItem modifier={modifier} depth={depth} />
        </Box>
        {modifier.orderModifiers && modifier.orderModifiers.length > 0 && (
          <Box key={`nested-modifiers-${modifier.modifierId}-${depth}`} sx={{ width: "100%" }}>
            <ModifierList modifiers={modifier.orderModifiers} depth={depth + 1} />
          </Box>
        )}
      </React.Fragment>
    ))}
  </>
));

const PriceInfo = memo(({ prices }: { prices: { itemPrice: number; modifierPrice: number; subtotalAmount: number } }) => (
  <>
    <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
      <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773', letterSpacing: '-.016rem'}}>
        Regular Price
      </Typography>
      <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773',letterSpacing: '-.016rem', width: '6.5rem', textAlign: 'end'}}>
        ${prices.itemPrice.toFixed(2)}
      </Typography>
    </Box>
    <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
      <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773', letterSpacing: '-.016rem'}}>
        Option Price
      </Typography>
      <Typography sx={{fontSize: '1.6rem', fontWeight: '500', fontStyle: 'normal', color: '#636773',letterSpacing: '-.016rem', width: '6.5rem', textAlign: 'end'}}>
        ${prices.modifierPrice.toFixed(2)}
      </Typography>
    </Box>
    <Typography sx={{fontSize: '2.0rem', fontWeight: '600', letterSpacing:'-.02rem', textAlign: 'right', mt: '.5rem'}}>
      ${prices.subtotalAmount.toFixed(2)}
    </Typography>
  </>
));

const QuantityControl = memo(({ 
  quantity, 
  onSubCount, 
  onAddCount 
}: { 
  quantity: number; 
  onSubCount: () => void; 
  onAddCount: () => void;
}) => {
  const lastClickTime = useRef(0);
  const isProcessing = useRef(false);
  const MIN_CLICK_INTERVAL = 50;

  const handleClick = useCallback((callback: () => void) => {
    const now = Date.now();
    if (now - lastClickTime.current < MIN_CLICK_INTERVAL || isProcessing.current) {
      return;
    }

    isProcessing.current = true;
    lastClickTime.current = now;

    requestAnimationFrame(() => {
      callback();
      isProcessing.current = false;
    });
  }, []);

  return (
    <Box sx={{
      display: 'flex', 
      flexDirection: 'row', 
      alignItems: 'center', 
      justifyContent: 'center', 
      gap: '1rem', 
      width: 'auto',
      flex: '0 0 auto',
      maxWidth: '20rem',
      padding: '.5rem .7rem', 
      borderRadius: '3.0rem', 
      backgroundColor: '#fff', 
      border: '2px solid #E9E9E9'
    }}>
      <div 
        style={{
          padding: '0rem',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          width: '4rem',
          height: '4rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="touch-area" onClick={() => handleClick(onSubCount)}>
          <MinusIcon fill="#000000" />
        </div>
      </div>
      <Typography sx={{
        fontSize: '2.2rem', 
        fontWeight: '700', 
        fontStyle: 'normal', 
        width: '3.5rem',
        flex: '0 0 auto',
        textAlign: 'center'
      }}>
        {quantity}
      </Typography>
      <div 
        style={{
          padding: '0rem',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          width: '4rem',
          height: '4rem',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="touch-area" onClick={() => handleClick(onAddCount)}>
          <AddIcon fill="#000000" />
        </div>
      </div>
    </Box>
  );
});

const OrderBox = memo((props: {setOpenOrderBox: any; checkLimitText: any; selectRequired: any; setSelectRequired: any}) => {
  const { setOpenOrderBox, checkLimitText, selectRequired, setSelectRequired} = props;
  const { orientation } = useWindowDimensions();
  const { userInfo, tableInfo, currentLng } = useInfoStore();
  const { preOrder, setOpenCart, setPreOrder, setSnackMode, setShowSnackbar, setLimitValue} = useOrderStore();
  const { itemDetail, setItemDetail, setSelectedItem } = useMenuStore();
  
  const storeId = userInfo?.storeId;
  const {t} =  useTranslation(`${storeId}`);
  const landMode = orientation === "landscape";
  const {height} = useWindowDimensions();

  // 선택된 modifier 목록 메모이제이션
  const selectedModifiers = useMemo(() => {
    if (!preOrder?.orderModifiers) return [];
    return preOrder.orderModifiers;
  }, [preOrder?.orderModifiers]);

  // 가격 계산 메모이제이션
  const prices = useMemo(() => {
    if (!preOrder) return { itemPrice: 0, modifierPrice: 0, subtotalAmount: 0 };
    return {
      itemPrice: (preOrder.itemPrice || 0) / 100,
      modifierPrice: (preOrder.modifierPrice || 0) / 100,
      subtotalAmount: (preOrder.subtotalAmount || 0) / 100
    };
  }, [preOrder?.itemPrice, preOrder?.modifierPrice, preOrder?.subtotalAmount]);

  // 수량 변경 핸들러
  const handleSubCount = useCallback(() => {
    if (!itemDetail || !preOrder) return;

    const itemQuantity = itemDetail.quantity === itemDetail.minRequired ? 
      itemDetail.minRequired : 
      itemDetail.quantity - 1;

    if (itemDetail.minRequired && itemQuantity < itemDetail.minRequired) {
      setLimitValue(itemDetail.minRequired);
      setSnackMode(13);
      setShowSnackbar(true);
      return;
    }

    setPreOrder({ ...preOrder, quantity: itemQuantity });
    setItemDetail({ ...itemDetail, quantity: itemQuantity });
  }, [itemDetail, preOrder, setPreOrder, setItemDetail, setLimitValue, setSnackMode, setShowSnackbar]);

  const handleAddCount = useCallback(() => {
    if (!itemDetail || !preOrder) return;

    const newQuantity = preOrder.quantity + 1;
    const maxLimit = userInfo?.isAyce ? itemDetail.maxAllowedPerTable : itemDetail.maxAllowed;

    if (maxLimit && newQuantity > maxLimit) {
      setLimitValue(maxLimit);
      setSnackMode(userInfo?.isAyce ? 12 : 11);
      setShowSnackbar(true);
      return;
    }

    setPreOrder({ ...preOrder, quantity: newQuantity });
    setItemDetail({ ...itemDetail, quantity: newQuantity });
  }, [itemDetail, preOrder, userInfo?.isAyce, setPreOrder, setItemDetail, setLimitValue, setSnackMode, setShowSnackbar]);

  // 삭제 핸들러
  const handleDeleteItem = useCallback(() => {
    if (!itemDetail || !preOrder) return;

    const updatedDetail = {
      ...itemDetail,
      modifierGroups: itemDetail.modifierGroups?.map(group => ({
        ...group,
        modifiers: group.modifiers.map(modifier => ({
          ...modifier,
          quantity: 0,
          modifiers: [],
        })),
      })) || [],
    };

    setItemDetail(updatedDetail);
    setPreOrder({
      ...preOrder,
      quantity: itemDetail.minRequired || 1,
      orderModifiers: []
    });
  }, [itemDetail, preOrder, setItemDetail, setPreOrder]);

  // 주문 확인 핸들러
  const handleConfirmPreOrder = useCallback(async () => {
    if (!preOrder) return;

    // Required modifier 체크
    if (preOrder.hasModifier && !selectRequired) {
      setSnackMode(0);
      setShowSnackbar(true);
      return;
    }

    // Optional modifier의 min 조건 체크
    // if (preOrder.hasModifier && itemDetail?.modifierGroups) {
    //   const checkOptionalMinRequirements = (groups: any[]): { isValid: boolean; minValue?: number; groupName?: string } => {
    //     for (const group of groups) {
    //       if (!group.modifiers) continue;

    //       // 현재 그룹의 선택된 수량 계산
    //       const currentGroupQuantity = group.modifiers.reduce(
    //         (sum: number, mod: any) => sum + (mod.quantity || 0),
    //         0
    //       );

    //       // Optional 그룹이지만 minRequired가 설정된 경우 체크
    //       // 단, 하나라도 선택된 경우에만 min 조건을 체크
    //       if (!group.isRequired && group.minRequired !== null && group.minRequired > 0 && currentGroupQuantity > 0) {
    //         if (currentGroupQuantity < group.minRequired) {
    //           return { 
    //             isValid: false, 
    //             minValue: group.minRequired,
    //             groupName: group.modifierGroupName 
    //           };
    //         }
    //       }

    //       // 재귀적으로 하위 modifierGroup 체크
    //       for (const mod of group.modifiers) {
    //         if (mod.modifiers && mod.modifiers.length > 0) {
    //           const result = checkOptionalMinRequirements(mod.modifiers);
    //           if (!result.isValid) {
    //             return result;
    //           }
    //         }
    //       }
    //     }
    //     return { isValid: true };
    //   };

    //   const optionalCheck = checkOptionalMinRequirements(itemDetail.modifierGroups);
    //   if (!optionalCheck.isValid) {
    //     // 새로운 snackMode 추가 (15번)
    //     setSnackMode(15);
    //     setLimitValue(optionalCheck.minValue || 0);
    //     setShowSnackbar(true);
    //     return;
    //   }
    // }

    try {
      await useOrderStore.getState().setCart({ item: preOrder, type: 'add' });
      
      if (useOrderStore.getState().updateCart) {
        setSnackMode(3);
        setShowSnackbar(true);
      }
    } catch (error) {
      logger.error('Failed to update cart', error);
    }

    setSelectRequired(false);
    setSelectedItem(null)
    setItemDetail(null);
  }, [preOrder, selectRequired, setSelectRequired, setItemDetail]);
  // }, [preOrder, selectRequired, setSelectRequired, setItemDetail, itemDetail, setSnackMode, setShowSnackbar, setLimitValue]);

  if (!preOrder || !itemDetail) return null;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: height,  // 상단 닫기 버튼 영역(7rem) 제외
      width: '100%',
      position: 'relative',
      background: '#fdfdfd',
      borderRadius: '.5rem',
      overflow: 'hidden',
    }}>
      {/* 스크롤 영역 */}
      <Box sx={{
        height: (itemDetail.maxAllowed || itemDetail.minRequired! > 1) ? 'calc(100% - 41rem)' : 'calc(100% - 36rem)', 
        overflow: 'auto', 
        '&::-webkit-scrollbar': {
          width: '.4rem'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '.2rem'
        }
      }}>
        <Box sx={{justifyContent: 'center', mx: '2.5rem', paddingTop: '.4rem'}}>
          <Typography sx={{fontSize: '2.1rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.02rem'}}>
            {preOrder.itemId ? t(`itemNames.${preOrder.itemId}.${currentLng}`) : ''}
          </Typography>
          
          {selectedModifiers.length > 0 && (
            <Box sx={{background: '#F0F0F0', borderRadius: '1.0rem', padding: '1.3rem 1.2rem', alignItems: 'flex-start', gap: '1.0rem', mt: '1rem'}}>
              <ModifierList modifiers={selectedModifiers} />
            </Box>
          )}

          <Box sx={{mt: '1rem'}}>
            <PriceInfo prices={prices} />
          </Box>
        </Box>
      </Box>

      {/* 하단 컨트롤 영역 */}
      <Box sx={{
        position: 'absolute', bottom: '0', left: '0', right: '0', display: 'flex',
        width: '100%',
        background: '#fff',
        borderTop: '1px solid #E9E9E9',
        padding: '2rem',
        flexDirection: 'column',
        gap: '1.5rem',
        minHeight: landMode ? (itemDetail.maxAllowed || itemDetail.minRequired! > 1) ? '40rem': '35rem' : '25rem',
        zIndex: 1
      }}>
        {/* 제한 조건 표시 */}
        {(itemDetail.maxAllowed || itemDetail.minRequired! > 1) && (
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center'}}>
            <img
              style={{width: '2.4rem', height: '2.4rem'}}
              src={process.env.PUBLIC_URL+'/assets/images/warning_r.svg'}
              alt="warning"
            />
            <Typography sx={{fontSize: '2.0rem', fontWeight: '600', color: '#F72E13', ml: '1rem'}}>
              {checkLimitText()}
            </Typography>
          </Box>
        )}

        {/* 수량 조절 영역 */}
        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '3.0rem'}}>
          <ButtonBase
            sx={{
              color:'#F72E13',
              borderRadius: '3.0rem',
              border: '2px solid #F72E13',
              backgroundColor: '#fff',
              width: '12.6rem',
              p: '.9rem 2.1rem',
              gap: '1.0rem',
              alignItems: 'center'
            }}
            onClick={handleDeleteItem}
          >
            <Typography sx={{color:'#F72E13', fontSize: '1.6rem', fontStyle: 'normal', fontWeight: '600', letterSpacing: '-.016rem'}}>
              Remove
            </Typography>
          </ButtonBase>

          <QuantityControl
            quantity={preOrder.quantity}
            onSubCount={handleSubCount}
            onAddCount={handleAddCount}
          />
        </Box>

        {/* Subtotal 영역 */}
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
          <Divider sx={{width: '100%', height: '2px', backgroundColor: '#000'}}/>
          
          <Box sx={{display: 'flex', flexDirection: 'row', width: '100%', mt: '1.5rem', justifyContent: 'flex-end', alignItems: 'center'}}>
            <Typography sx={{fontSize: '2.2rem', fontWeight: '500', fontStyle: 'normal', letterSpacing: '-.022rem', lineHeight: '2.4rem'}}>
              Qty 
            </Typography>
            <Typography sx={{fontSize: '2.4rem', fontWeight: '700', fontStyle: 'normal', width: '6.7rem', textAlign: 'end', lineHeight: '2.4rem', mr: '1.0rem'}}>
              {preOrder.quantity}
            </Typography>
          </Box>

          <Box sx={{width: '100%', mt: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <Box sx={{display: 'flex', width: '100%', flexDirection: 'row', padding: '.7rem .4rem', justifyContent: 'flex-end', height: '3.6rem', backgroundColor: '#FFD600'}}>
              <Typography sx={{fontSize: '2.2rem', fontWeight: '600', fontStyle: 'normal', letterSpacing: '-.022rem', lineHeight: '2.2rem', width: '10.2rem'}}>
                Subtotal 
              </Typography>
              <Typography sx={{fontSize: '2.6rem', fontWeight: '700', fontStyle: 'normal', letterSpacing: '-.026rem', lineHeight: '2.2rem', width: '12.3rem', textAlign: 'end'}}>
                ${(prices.subtotalAmount * preOrder.quantity).toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Confirm 버튼 */}
        {itemDetail.useAgeVerification && itemDetail.requiresAgeVerification && !itemDetail.isAgeVerified ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            height: '5.5rem',
            width: '100%'
          }}>
            {/* <img 
              style={{marginRight: '1.3rem', width: '4.0rem', height: '4.0rem'}}
              src={process.env.PUBLIC_URL+'/assets/images/21+.svg'}
              alt="cart"
            /> */}
            <Typography sx={{fontSize: '2.0rem', fontWeight:'600', textWrap: 'wrap'}}>
              Please ask your server to order this item.
            </Typography>
          </Box>
        ) : (
          <div className="touch-area" onClick={handleConfirmPreOrder} style={{width: '100%'}}>
            <ButtonBase sx={{
              backgroundColor: '#F72E13',
              p: '1.0rem 2.0rem',
              borderRadius: '4.0rem',
              width: '100%'
            }}>
              <Typography sx={{color: '#fff', fontSize: '18px', fontStyle: 'normal', fontWeight: '600'}}>
                Confirm & Add to Cart
              </Typography>
            </ButtonBase>
          </div>
        )}
      </Box>
    </Box>
  );
});

export default OrderBox;


